import { useEffect, useState } from "react";
import { getProductFunnelAngles, type ProductFunnelAngle } from "@/services/ads/productFunnelAngles";
import { useAuth } from "@/context/AuthContext";
import { dictionary } from "@/utils/dictionary";

interface ProductFunnelAnglesProps {
  productId?: string | null;
  onUseAngle?: (angle: { name: string; description: string }) => void;
  className?: string;
}

// Panel de referencia: muestra los embudos que ya usan este producto y su ángulo
// (nº de embudo + ángulo). Se alimenta de GET /websites/summary filtrado por producto.
export const ProductFunnelAngles: React.FC<ProductFunnelAnglesProps> = ({
  productId,
  onUseAngle,
  className,
}) => {
  const { language } = useAuth();
  const [items, setItems] = useState<ProductFunnelAngle[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!productId) {
      setItems([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getProductFunnelAngles(productId)
      .then((res) => {
        if (!cancelled) setItems(res);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [productId]);

  // Panel secundario: no ocupa espacio si no hay producto o no hay embudos.
  if (!productId || loading || items.length === 0) return null;

  return (
    <div className={`rounded-2xl border border-gray-200 bg-white p-4 ${className ?? ""}`}>
      <p className="mb-3 text-sm font-semibold text-gray-800">
        {dictionary["product-funnels-angles-title"][language]}
      </p>
      <ul className="flex flex-col gap-2">
        {items.map((item) => (
          <li
            key={item.websiteId}
            className="flex items-center justify-between gap-3 rounded-xl border border-gray-100 bg-gray-50 px-3 py-2"
          >
            <div className="min-w-0">
              <span className="text-xs font-medium text-gray-400">
                {dictionary["funnel-number-label"][language]}
                {item.funnelNumber ? ` #${item.funnelNumber}` : ""}
              </span>
              <p className="truncate text-sm text-gray-800">
                {item.angleName || dictionary["product-funnels-angle-empty"][language]}
              </p>
            </div>
            {onUseAngle && item.angleName && (
              <button
                type="button"
                onClick={() =>
                  onUseAngle({ name: item.angleName, description: item.angleDescription })
                }
                className="shrink-0 rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-gray-800"
              >
                {dictionary["product-funnels-use-angle"][language]}
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};
